import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, retry, timer, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CurrentUser } from '../models/current-user.model';

const ROLE_RANKS: Record<string, number> = {
  Admin: 0,
  Reviewer: 1,
  Preparer: 2,
};

/**
 * Errors that suggest the backend or database is unreachable or still waking up
 * (network failure / gateway timeout / 5xx), as opposed to an auth rejection (4xx).
 * Status 0 covers both network failures and CORS-stripped error responses.
 */
function isTransientError(status: number | undefined): boolean {
  return status === 0 || (status !== undefined && status >= 500);
}

/**
 * Loads the current user's identity, role, and granted permissions from the backend
 * once at startup. Exposes permission checks as signals for template-level gating.
 * Backend enforcement is the source of truth; these checks drive UX only.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly http = inject(HttpClient);

  readonly currentUser = signal<CurrentUser | null>(null);
  readonly loaded = signal(false);
  /** Why the last load() failed: 'transient' = backend/DB unreachable (cold start), 'auth' = rejected. */
  readonly loadFailure = signal<'transient' | 'auth' | null>(null);
  readonly isAdmin = computed(() => this.currentUser()?.isAdmin ?? false);
  readonly isProvisioned = computed(() => this.currentUser()?.isProvisioned ?? false);
  readonly userId = computed(() => this.currentUser()?.userId ?? null);
  readonly role = computed(() => this.currentUser()?.role ?? null);
  private readonly permissions = computed(
    () => new Set(this.currentUser()?.permissions ?? []),
  );

  load(): Observable<void> {
    this.loadFailure.set(null);
    return this.http.get<CurrentUser>(`${environment.apiUrl}/me`).pipe(
      retry({
        count: 5,
        // Only retry transient failures — retrying a 401/403 just delays the real outcome.
        // Flat 8s delays: when the backend fails fast (connection refused, quick 40613
        // during resume), this gives ~40s+ of patience so a normal serverless wake
        // (30-90s) completes under the spinner instead of bailing to /waking-up early.
        delay: (error: HttpErrorResponse) => {
          if (!isTransientError(error?.status)) throw error;
          return timer(8000);
        },
      }),
      tap((user) => {
        this.currentUser.set(user);
        this.loaded.set(true);
      }),
      map(() => void 0),
      catchError((err: HttpErrorResponse) => {
        this.loadFailure.set(isTransientError(err?.status) ? 'transient' : 'auth');
        console.error('PermissionService: /api/me failed', err?.status);
        return of(void 0);
      }),
    );
  }

  reset(): void {
    this.currentUser.set(null);
    this.loaded.set(false);
    this.loadFailure.set(null);
  }

  has(permission: string): boolean {
    return this.permissions().has(permission);
  }

  canCreate(): boolean {
    return this.has('submissions.create');
  }

  canViewAll(): boolean {
    return this.has('submissions.view_all');
  }

  canEdit(submitterRole: string | null | undefined, submittedByUserId: string | null | undefined): boolean {
    return this.canActOnSubmission('submissions.edit', submitterRole, submittedByUserId);
  }

  /** Approve/reject authority: the review flag AND the rank test, never on one's own submission.
   * Mirrors the backend's CanReviewSubmissionAsync. */
  canReview(submitterRole: string | null | undefined, submittedByUserId: string | null | undefined): boolean {
    if (!this.has('submissions.review')) return false;
    const myRole = this.role();
    if (!myRole) return false;
    if (submittedByUserId && this.userId() === submittedByUserId) return false;
    const myRank = ROLE_RANKS[myRole];
    if (myRank === undefined) return false;
    if (!submitterRole) return true;
    const submitterRank = ROLE_RANKS[submitterRole];
    if (submitterRank === undefined) return true;
    return myRank <= submitterRank;
  }

  /** Reopen authority: the reopen flag grants reopening one's OWN submissions; anyone else's
   * additionally requires review authority over it. Mirrors the backend's
   * CanReopenSubmissionAsync — this composition is what keeps a Preparer from reopening
   * another Preparer's submission even though the rank test alone would allow it. */
  canReopen(submitterRole: string | null | undefined, submittedByUserId: string | null | undefined): boolean {
    if (!this.has('submissions.reopen')) return false;
    if (submittedByUserId && this.userId() === submittedByUserId) return true;
    return this.canReview(submitterRole, submittedByUserId);
  }

  canDelete(submitterRole: string | null | undefined, submittedByUserId: string | null | undefined): boolean {
    return this.canActOnSubmission('submissions.delete', submitterRole, submittedByUserId);
  }

  /** Checkout authority: the checkout flag grants locking one's OWN submissions; anyone else's
   * additionally requires review authority over it. Mirrors the backend's
   * CanCheckoutSubmissionAsync — a Preparer can only ever lock their own uploads. */
  canCheckout(submitterRole: string | null | undefined, submittedByUserId: string | null | undefined): boolean {
    if (!this.has('submissions.checkout')) return false;
    if (submittedByUserId && this.userId() === submittedByUserId) return true;
    return this.canReview(submitterRole, submittedByUserId);
  }

  private canActOnSubmission(
    permission: string,
    submitterRole: string | null | undefined,
    submittedByUserId: string | null | undefined,
  ): boolean {
    if (!this.has(permission)) return false;
    if (!submittedByUserId || this.userId() === submittedByUserId) return true;
    const myRole = this.role();
    if (!myRole) return false;
    const myRank = ROLE_RANKS[myRole];
    if (myRank === undefined) return false;
    if (!submitterRole) return true;
    const submitterRank = ROLE_RANKS[submitterRole];
    if (submitterRank === undefined) return true;
    return myRank <= submitterRank;
  }
}
